import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  Calendar,
  Settings,
  BarChart3,
  UserCheck,
  Clock,
  Target,
  UserPlus,
  Menu,
  X,
  CheckSquare,
  CreditCard,
  LifeBuoy,
} from "lucide-react";
import { LoginUserData } from "../LoginForm/LoginForm";
import { mockAPI } from "../../services/mockAPI";
import { realAPI } from "../../services/realAPI";
import TeamManagement from "./TeamManagement";
import SprintManagement from "./SprintManagement";
import UserManagement from "./UserManagement";
import AdminSettings from "./AdminSettings";
import QuotaManagement from "./QuotaManagement";
import PlanSubscription from "./PlanSubscription";
import { AlertCircle } from "lucide-react";
import SupportTickets from "./SupportTickets";

interface AdminDashboardProps {
  currentUser: LoginUserData;
  onLogout: () => void;
}

type AdminView =
  | "overview"
  | "teams"
  | "sprints"
  | "users"
  | "support"
  | "plans"
  | "settings";

interface DashboardStats {
  totalUsers: number;
  totalTeams: number;
  activeSprints: number;
  completedSprints: number;
  totalSprints: number;
  totalTasks: number;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({
  currentUser,
  onLogout,
}) => {
  const { organizationId, organization, id: currentUserId } = currentUser;
  const [currentView, setCurrentView] = useState<AdminView>("overview");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalTeams: 0,
    activeSprints: 0,
    completedSprints: 0,
    totalSprints: 0,
    totalTasks: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [lowBalanceWarning, setLowBalanceWarning] = useState<
    { type: string; balance: number; quota: number }[]
  >([]);
  const [quotaSummary, setQuotaSummary] = useState<{
    users?: { balance: number; quota: number };
    stories?: { balance: number; quota: number };
  } | null>(null);

  // Debug log to check currentUser
  console.log("AdminDashboard currentUser:", currentUser);

  const loadDashboardStats = useCallback(async () => {
    try {
      setIsLoading(true);

      // Debug logging to check organization ID
      console.log("currentUserId for stats:", currentUserId);
      console.log("currentUser.organizationId:", organizationId);
      console.log("currentUser.organization.id:", organization?.id);

      // Use currentUser.organization.id if organizationId is undefined
      const orgId = organizationId || organization?.id;

      if (!orgId) {
        throw new Error("No organization ID found");
      }

      // Convert orgId to string to ensure proper API call
      const orgIdString = orgId.toString();
      console.log("Using organization ID:", orgIdString);

      // Fetch organization info (for quotas) and stats in parallel
      const [orgInfo, stats] = await Promise.all([
        realAPI.getCurrentOrganization(),
        realAPI.getOrganizationStats(orgIdString),
      ]);
      console.log("Received org info:", orgInfo);
      console.log("Received stats from API:", stats);

      setStats({
        totalUsers: stats.totalUsers || 0,
        totalTeams: stats.totalTeams || 0,
        activeSprints: stats.activeSprints || 0,
        completedSprints: stats.completedSprints || 0,
        totalSprints: stats.totalSprints || 0,
        totalTasks: stats.totalTasks || 0,
      });

      // Compute low-balance warnings (threshold: <= 5)
      const threshold = 5;
      const warnings = (orgInfo?.progressivePlanQuotas || [])
        .filter(
          (q: any) =>
            typeof q.balance === "number" &&
            typeof q.quota === "number" &&
            q.balance <= threshold
        )
        .map((q: any) => ({
          type: q.progressivePricingType,
          balance: q.balance,
          quota: q.quota,
        }));
      setLowBalanceWarning(warnings);

      // Compute quota summary for USERS and STORIES
      const quotas = orgInfo?.progressivePlanQuotas || [];
      const usersQ = quotas.find(
        (q: any) => q.progressivePricingType === "USERS"
      );
      const storiesQ = quotas.find(
        (q: any) => q.progressivePricingType === "STORIES"
      );
      setQuotaSummary({
        users: usersQ
          ? { balance: usersQ.balance ?? 0, quota: usersQ.quota ?? 0 }
          : undefined,
        stories: storiesQ
          ? { balance: storiesQ.balance ?? 0, quota: storiesQ.quota ?? 0 }
          : undefined,
      });
    } catch (error) {
      console.error("Error loading dashboard stats:", error);
      // Fallback to mock API if real API fails
      try {
        const orgId = organizationId || organization?.id;
        const [users, teams, sprints] = await Promise.all([
          mockAPI.getUsersByOrganization(orgId),
          mockAPI.getTeamsByOrganization(orgId),
          mockAPI.getSprintsByOrganization(orgId),
        ]);

        setStats({
          totalUsers: users.length,
          totalTeams: teams.length,
          activeSprints: sprints.filter((s) => s.status === "active").length,
          completedSprints: sprints.filter((s) => s.status === "completed")
            .length,
          totalSprints: sprints.length,
          totalTasks: 0, // Mock API doesn't have task count
        });
      } catch (fallbackError) {
        console.error("Error loading fallback stats:", fallbackError);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, organizationId, organization?.id]);

  useEffect(() => {
    loadDashboardStats();
  }, [loadDashboardStats]);

  const menuItems = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "users", label: "Users", icon: UserPlus },
    { id: "teams", label: "Teams", icon: Users },
    { id: "sprints", label: "Sprints", icon: Calendar },
    { id: "plans", label: "Quotas & Balance", icon: CreditCard },
    { id: "support", label: "Support", icon: LifeBuoy },
    // { id: "projects", label: "Projects", icon: Folders },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  const renderContent = () => {
    switch (currentView) {
      case "users":
        return (
          <UserManagement
            currentUser={currentUser}
            onStatsUpdate={loadDashboardStats}
            onGoToQuotas={() => setCurrentView("plans")}
          />
        );
      case "teams":
        return (
          <TeamManagement
            currentUser={currentUser}
            onStatsUpdate={loadDashboardStats}
          />
        );
      case "sprints":
        return (
          <SprintManagement
            currentUser={currentUser}
            onStatsUpdate={loadDashboardStats}
          />
        );
      case "support":
        return <SupportTickets currentUser={currentUser} />;
      case "plans":
        return (
          <>
            <QuotaManagement onUpdate={loadDashboardStats} />
            <PlanSubscription onUpdate={loadDashboardStats} />
          </>
        );
      case "settings":
        return <AdminSettings currentUser={currentUser} onLogout={onLogout} />;
      case "overview":
      default:
        return (
          <>
            {/* Quota Summary */}
            {quotaSummary && (quotaSummary.users || quotaSummary.stories) && (
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-blue-900 text-sm">
                  {quotaSummary.stories && (
                    <div>
                      You have {quotaSummary.stories.balance} stories left out
                      of {quotaSummary.stories.quota} stories
                    </div>
                  )}
                  {quotaSummary.users && (
                    <div>
                      You have {quotaSummary.users.balance} users left out of{" "}
                      {quotaSummary.users.quota} users
                    </div>
                  )}
                  <button
                    className="mt-3 inline-flex items-center px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
                    onClick={() => setCurrentView("plans")}
                  >
                    Top up now
                  </button>
                </div>
              </div>
            )}
            {/* Low balance warnings */}
            {lowBalanceWarning.length > 0 && (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-amber-800 font-semibold">
                      Low quota balance
                    </h3>
                    <ul className="mt-2 text-amber-800 text-sm list-disc pl-5">
                      {lowBalanceWarning.map((w, idx) => (
                        <li key={idx}>
                          {w.type} balance is {w.balance} of {w.quota}. Consider
                          topping up.
                        </li>
                      ))}
                    </ul>
                    <button
                      className="mt-3 inline-flex items-center px-3 py-1.5 rounded-md bg-amber-600 text-white text-sm hover:bg-amber-700"
                      onClick={() => setCurrentView("plans")}
                    >
                      Go to Quotas & Balance
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      Total Users
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {stats.totalUsers}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <UserCheck className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      Active Teams
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {stats.totalTeams}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      Active Sprints
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {stats.activeSprints}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Target className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      Total Sprints
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {stats.totalSprints}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <CheckSquare className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      Total Tasks
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {stats.totalTasks}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Quick Actions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setCurrentView("users")}
                  className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <UserPlus className="h-5 w-5 text-blue-600 mr-3" />
                  <span className="text-blue-700 font-medium">
                    Add New User
                  </span>
                </button>
                <button
                  onClick={() => setCurrentView("teams")}
                  className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <Users className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-green-700 font-medium">
                    Create Team
                  </span>
                </button>
                <button
                  onClick={() => setCurrentView("sprints")}
                  className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <Calendar className="h-5 w-5 text-purple-600 mr-3" />
                  <span className="text-purple-700 font-medium">
                    Plan Sprint
                  </span>
                </button>
              </div>
            </div>
          </>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden mr-3 p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                Kanbello Admin
              </h1>
              <span className="ml-3 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                {currentUser.organization?.name}
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="hidden sm:block text-sm text-gray-700">
                Welcome, {currentUser.firstName} {currentUser.lastName}
              </span>
              <button
                onClick={onLogout}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <nav className="hidden md:block w-64 bg-white shadow-sm">
          <div className="p-4">
            <ul className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => setCurrentView(item.id as AdminView)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        currentView === item.id
                          ? "bg-blue-100 text-blue-700"
                          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                      }`}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            <div
              className="fixed inset-0 bg-black opacity-50"
              onClick={() => setIsMobileMenuOpen(false)}
            ></div>
            <nav className="relative w-64 bg-white shadow-lg">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Navigation
                  </h2>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <ul className="space-y-2">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.id}>
                        <button
                          onClick={() => {
                            setCurrentView(item.id as AdminView);
                            setIsMobileMenuOpen(false);
                          }}
                          className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            currentView === item.id
                              ? "bg-blue-100 text-blue-700"
                              : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                          }`}
                        >
                          <Icon className="mr-3 h-5 w-5" />
                          {item.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </nav>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8">{renderContent()}</main>
      </div>
    </div>
  );
};

export default AdminDashboard;
